import React, { useState, useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import _ from 'lodash'
import queryString from 'query-string'
import {
  Axios,
  Checkbox,
  DataTable,
  Grid,
  Table,
  ConditionWrapper,
  BaseForm as Form,
  Segment,
} from 'component/base'

export const KinyukikanShiten = withRouter(props => {
  const {
    history,
    enableQueryCondition,
    multiple,
    selectedId,
    onSelectChange,
    refresh,
    kinyukikan_id,
  } = props

  const query = useMemo(() => queryString.parse(window.location.search), [
    window.location.search,
  ])

  const path = useMemo(() => window.location.pathname, [
    window.location.pathname,
  ])

  const defaultCondition = useMemo(() => {
    return {}
  }, [])

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [displayData, setdisplayData] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [condition, setCondition] = useState(
    _.assign({}, defaultCondition, enableQueryCondition ? query : {})
  )

  useEffect(() => {
    setSelectedIds(
      multiple
        ? selectedId && _.isArray(selectedId)
          ? selectedId
          : []
        : selectedId
          ? [selectedId]
          : []
    )
  }, [selectedId])

  useEffect(() => {
    onSelectChange(
      selectedIds,
      _.filter(data, row => _.find(selectedIds, id => row.id === id)),
      data
    )
  }, [selectedIds])

  useEffect(() => {
    setCondition(condition =>
      enableQueryCondition
        ? _(condition).assign(query).value()
        : _(condition).value()
    )
  }, [defaultCondition, enableQueryCondition, query])

  useEffect(() => {
    callApi(condition)
    setSelectedIds([])
  }, [condition, refresh])

  const handleConditionChange = (condition, isOnClear) => {
    setCondition(isOnClear ? defaultCondition : condition)
    if (enableQueryCondition)
      isOnClear
        ? history.push(`${path}`)
        : history.push(`${path}?${queryString.stringify(condition)}`)
  }

  const callApi = async condition => {
    setLoading(true)
    const { data, error } = await Axios.ajax.get(
      `/api/staff/master/kinyukikan_shiten?kinyukikan_id=${kinyukikan_id}`,
      {
        params: { ...condition },
      }
    )
    setLoading(false)
    if (error) return
    setData(data)
    setSelectedIds([])
  }

  const handleChangeSelect = (selectedId, selected) => {
    setSelectedIds(selectedIds =>
      selected
        ? multiple
          ? _.union(selectedIds, [selectedId])
          : [selectedId]
        : _.filter(selectedIds, id => id !== selectedId)
    )
  }

  const TableRowWithProps = useCallback(
    props => (
      <TableRow
        {...props}
        history={history}
        handleChangeSelect={handleChangeSelect}
      />
    ),
    [history, handleChangeSelect]
  )

  useEffect(
    () =>
      setdisplayData(
        _(data).orderBy(['kinyukikan_shiten_id'], ['asc']).value()
      ),
    [data]
  )

  return (
    <>
      <Segment basic loading={loading} style={{ padding: '0em' }}>
        <ConditionWrapper
          defaultCondition={defaultCondition}
          condition={condition}
          onConditionChange={handleConditionChange}
          FormElement={SearchElement}
        >
          <DataTable
            name='kinyukikan_shiten'
            sortable
            filterable
            tableHeight='50vh'
            tableHeader={TableHeader}
            tableRow={TableRowWithProps}
            data={displayData}
            bottomControl={<></>}
            selectedIds={selectedIds}
          />
        </ConditionWrapper>
      </Segment>
    </>
  )
})

KinyukikanShiten.propTypes = {
  enableQueryCondition: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  onSelectChange: PropTypes.func,
}

KinyukikanShiten.defaultProps = {
  enableQueryCondition: false,
  multiple: false,
  selectedValue: '',
  onSelectChange: () => { },
}

const TableHeader = props => {
  const { column, direction, withOrder, handleSort } = props

  return withOrder(
    <Table.Row>
      <Table.HeaderCell style={{ width: '3.0rem' }} content='#' />
      <Table.HeaderCell
        style={{ width: '10.0rem' }}
        sorted={column === 'shiten_cd' ? direction : null}
        onClick={() => handleSort('shiten_cd')}
        content='支店コード'
      />
      <Table.HeaderCell
        style={{ width: '17.0rem' }}
        sorted={column === 'shiten_name' ? direction : null}
        onClick={() => handleSort('shiten_name')}
        content='支店名称'
      />
      <Table.HeaderCell
        style={{ width: '17.0rem' }}
        sorted={column === 'shiten_kana' ? direction : null}
        onClick={() => handleSort('shiten_kana')}
        content='支店カナ'
      />
    </Table.Row>
  )
}

const TableRow = props => {
  const { withOrder, data, selectedIds, handleChangeSelect } = props || {}

  const { id, shiten_cd, shiten_name, shiten_kana } = data || {}

  const [isSelected, setIsSelected] = useState(
    selectedIds.indexOf(id) >= 0 ? true : false
  )

  const checkboxChange = (event, { value, checked }) => {
    event.stopPropagation()
    setIsSelected(checked)
    handleChangeSelect(value, checked)
  }

  return withOrder(
    <Table.Row
      positive={isSelected}
      onClick={() => {
        setIsSelected(state => !state)
        handleChangeSelect(id, !isSelected)
      }}
    >
      <Table.Cell
        style={{ width: '3.0rem' }}
        textAlign='center'
        content={
          <Checkbox checked={isSelected} onChange={checkboxChange} value={id} />
        }
      />
      <Table.Cell style={{ width: '10.0rem' }} content={shiten_cd} />
      <Table.Cell style={{ width: '17.0rem' }} content={shiten_name} />
      <Table.Cell style={{ width: '17.0rem' }} content={shiten_kana} />
    </Table.Row>
  )
}

const SearchElement = props => {
  const { nav, condition } = props

  const { shiten_name, shiten_kana } = condition

  return (
    <>
      <Grid.Column style={{ display: !nav || shiten_name ? 'block' : 'none' }}>
        <Form.Textbox.Standard
          value={shiten_name}
          label='支店名称'
          name='shiten_name'
        />
      </Grid.Column>
      <Grid.Column style={{ display: !nav || shiten_kana ? 'block' : 'none' }}>
        <Form.Textbox.Standard
          value={shiten_kana}
          label='支店カナ'
          name='shiten_kana'
        />
      </Grid.Column>
    </>
  )
}
